<template>
	<DashboardModal
		ref="modal"
		dialog
		width="700px"
		@modal-toggle="$emit('modal-toggle')"
	>
		<DashboardFormTitle :class="$style['add-client-form__title']">
			<span v-if="!client">
				{{ $t("clients-view.new-client.titles.new-client") }}
			</span>
			<span v-else>
				{{ $t("clients-view.new-client.titles.edit-client") }}
			</span>
		</DashboardFormTitle>
		<Spinner v-if="isRequesting" :center="true" />
		<template v-if="!isRequesting">
			<div :class="$style['add-client-form']">
				<FormSection :title="$t('common.fields.settings')">
					<div :class="$style['checkboxes']">
						<div :class="$style['checkboxes__item']">
							<BaseCheckbox
								:checked="departmentClient"
								:disabled="!isFormEditable"
								:label="$t('common.fields.department-client')"
								@change="handleDepartmentClientChange"
							/>
						</div>
						<div :class="$style['checkboxes__item']">
							<BaseCheckbox
								:checked="companyClient"
								:disabled="businessClient || !isFormEditable"
								:label="$t('orders-view.new-order.fields.client-types.business')"
								@change="handleCompanyClientChange"
							/>
						</div>
						<div :class="$style['checkboxes__item']">
							<BaseCheckbox
								:checked="locked"
								:disabled="!isFormEditable || !canEditLockedField"
								:label="$t('common.fields.read-only')"
								@change="handleLockedChange"
							/>
						</div>
					</div>
				</FormSection>
				<ClientInfo
					ref="clientInfo"
					:disabled="!isFormEditable"
					:locked="locked"
					:countries="countries"
					@shake="$refs.modal.shake()"
				/>
				<CompanyInfo
					v-if="companyClient"
					ref="companyInfo"
					:disabled="!isFormEditable"
					:countries="countries"
					@shake="$refs.modal.shake()"
				/>
				<FormSection style="margin-top: 20px;" :title="$t('orders-view.new-order.title.delivery-info')">
					<div :class="$style['checkboxes']">
						<div :class="$style['checkboxes__item']">
							<BaseCheckbox
								:disabled="!isFormEditable"
								:label="
									companyClient
										? $t('common.fields.same-as-company-address')
										: $t('common.fields.same-as-client-address')
								"
								:checked="sameDeliveryAddress"
								@change="sameDeliveryAddress = !sameDeliveryAddress"
							/>
						</div>
					</div>
					<AddressInfo
						v-show="!sameDeliveryAddress"
						ref="addressInfo"
						:disabled="!isFormEditable"
						:countries="countries"
						:embedded="true"
						@shake="$refs.modal.shake()"
					/>
				</FormSection>
				<FormSection :title="$t('clients-view.inner.payment-settings.title')">
					<div :class="$style['checkboxes']">
						<div :class="$style['checkboxes__item']">
							<BaseCheckbox
								:disabled="!isFormEditable || !canEditPaymentSettings"
								:label="$t('clients-view.inner.payment-settings.fields.delay-payment')"
								:checked="delayPayment"
								@change="delayPayment = !delayPayment"
							/>
						</div>
					</div>
					<BaseTextField
						v-if="delayPayment"
						v-model="delayPaymentDays"
						:disabled="!isFormEditable || !canEditPaymentSettings"
						:label-text="$t('clients-view.inner.payment-settings.fields.delay-payment-days')"
						type="number"
						name="delay-payment-days"
					/>
				</FormSection>
			</div>
			<div :class="$style['add-client-form__actions']">
				<BaseButton
					:disabled="!isFormEditable || errors.any()"
					:loading="requesting"
					primary
					type="submit"
					@click="formSubmit"
				>
					{{ $t("clients-view.new-client.actions.submit") }}
				</BaseButton>
			</div>
		</template>
	</DashboardModal>
</template>
<script>
import DashboardModal from '@/components/Layout/Dashboard/Modal'
import FormSection from '@/components/Essentials/FormSection'
import DashboardFormTitle from '@/components/Layout/Dashboard/Form/FormTitle'
import { mapActions, mapGetters, mapMutations } from '@/store/modules/Clients/helpers'
import { ADD_CLIENT, EDIT_CLIENT } from '@/store/modules/Clients/action-types'
import { UPDATE_CLIENT_FORM, CLEAR_CLIENT_FORM } from '@/store/modules/Clients/mutation-types'
import AddressInfo from './AddressInfo'
import CompanyInfo from './CompanyInfo'
import ClientInfo from './ClientInfo'

// import Tabs from '@/components/Essentials/Tabs/Tabs'
// import Tab from '@/components/Essentials/Tabs/Tab'

import Spinner from '@c:essentials/Spinner'
import ApolloClient from '@/packages/apollo-client'
import FetchCountriesForInput from '@/store/modules/Locations/Schema/Queries/FetchCountriesForInput.gql'


export default {
	name: 'AddClient',
	components: {
		DashboardModal,
		DashboardFormTitle,
		FormSection,
		// Tabs,
		// Tab,
		AddressInfo,
		CompanyInfo,
		ClientInfo,
		Spinner
	},
	props: {
		refetch: {
			type: Boolean,
			default: true
		},
		department: {
			type: [String, Number],
			default: null
		},
		client: {
			type: [Object, String],
			default: null
		},
		businessClient: {
			type: Boolean,
			default: false
		}
	},

	data: () => ({
		requesting: false,
		isRequesting: false,
		companyClient: false,
		sameDeliveryAddress: true,
		departmentClient: false,
		locked: false,
		delayPayment: false,
		delayPaymentDays: null,
		countries: []
	}),



	computed: {
		...mapGetters({
			editClientForm: 'editClientForm'
		}),

		isFormEditable () {
			return !this.client || this.client.editable !== false
		},

		permissionDepartmentId () {
			return this.client && this.client.departmentId
				? this.client.departmentId
				: this.department ?? this.$store.getters['Auth/department']?.id
		},

		canEditPaymentSettings () {
			return this.$auth().canEditClientFinancialFields(this.permissionDepartmentId)
		},

		canEditLockedField () {
			return this.$auth().canEditClientLockedField(this.permissionDepartmentId)
		}
	},

	beforeDestroy () {
		this.clearClientForm({})
	},


	async created () {
		if (this.businessClient) {
			this.companyClient = true
		}
		if (this.client && this.client.id) {
			this.sameDeliveryAddress = false
			this.companyClient = this.client.companyClient || !!this.client.companyName || this.businessClient
			const {
				address,
				city,
				companyAddress,
				companyCity,
				companyCode,
				companyCountry,
				companyEmail,
				companyName,
				companyPhoneNumber,
				companyPostalCode,
				companyVatCode,
				country,
				deliveryAddress,
				deliveryCity,
				deliveryCountry,
				deliveryEmail,
				deliveryName,
				deliveryPhoneNumber,
				deliveryPostalCode,
				departmentClient,
				departmentId,
				email,
				id,
				name,
				nickname,
				phoneNumber,
				postalCode,
				surname,
				delayPayment,
				delayPaymentDays,
				locked
			} = this.client

			this.departmentClient = departmentClient || false
			this.locked = !!locked
			this.delayPayment = delayPayment || false
			this.delayPaymentDays = delayPaymentDays === 0 ? 0 : (delayPaymentDays || null)

			this.updateClientForm({
				address,
				city,
				companyAddress,
				companyCity,
				companyCode,
				companyCountry,
				companyEmail,
				companyName,
				companyPhoneNumber,
				companyPostalCode,
				companyVatCode,
				country,
				deliveryAddress,
				deliveryCity,
				deliveryCountry,
				deliveryEmail,
				deliveryName,
				deliveryPhoneNumber,
				deliveryPostalCode,
				departmentClient,
				departmentId,
				email,
				id,
				name,
				nickname,
				phoneNumber,
				postalCode,
				surname
			})

		}
		this.isRequesting = true
		await this.fetchCountries()
		this.isRequesting = false
	},



	methods: {
		...mapActions({
			addClient: ADD_CLIENT,
			editClient: EDIT_CLIENT
		}),
		...mapMutations({
			updateClientForm: UPDATE_CLIENT_FORM,
			clearClientForm: CLEAR_CLIENT_FORM
		}),

		async fetchCountries () {
			this.countriesRequesting = true
			const { data: {
				countries: {
					nodes: countries
				}
			} } = await ApolloClient.query({
				query: FetchCountriesForInput,
				fetchPolicy: 'cache-first'
			})
			this.countries = countries.map(country => ({
				id: country.code,
				name: country.name
			}))
			this.countriesRequesting = false
		},

		handleCheckboxChange (value, key) {
			this[key] = value?.target ? value.target.checked : !!value
		},

		handleDepartmentClientChange (value) {
			this.handleCheckboxChange(value, 'departmentClient')
		},

		handleCompanyClientChange (value) {
			this.handleCheckboxChange(value, 'companyClient')
		},

		handleLockedChange (value) {
			this.handleCheckboxChange(value, 'locked')
		},

		handleSameDeliveryAddressChange (value) {
			this.handleCheckboxChange(value, 'sameDeliveryAddress')
		},



		async formSubmit () {
			this.requesting = true
			try {
				if (this.$refs.clientInfo) {
					const valid = await this.$refs.clientInfo.validate(this.locked)
					if (!valid) {
						this.requesting = false
						throw new Error(this.$t(
							this.locked
								? 'common.messages.locked-client-name-required'
								: 'common.messages.fill-required-fields'
						))
					}

				}
				if (this.$refs.companyInfo && this.companyClient) {
					const valid = await this.$refs.companyInfo.validate(this.locked)
					if (!valid) {
						this.requesting = false
						throw new Error(this.$t('common.messages.fill-required-fields'))
					}

				}
				if (this.$refs.addressInfo && !this.sameDeliveryAddress) {
					const valid = await this.$refs.addressInfo.validate(this.locked)
					if (!valid) {
						this.requesting = false
						throw new Error(this.$t('common.messages.fill-required-fields'))
					}
				}
				const departmentId = this.department !== null && this.department !== undefined ? this.department : (this.$store.getters['Auth/department']?.id || null)
				const payload = {
					departmentId,
					...(!this.companyClient
						? Object.fromEntries(
							Object.entries(this.editClientForm).filter(([key]) => !key.includes('company'))
						)
						: this.editClientForm
					),
					departmentClient: this.departmentClient,
					companyClient: this.companyClient,
					locked: this.locked,
					...(this.canEditPaymentSettings
						? {
							delayPayment: this.delayPayment,
							delayPaymentDays: this.delayPayment && this.delayPaymentDays ? parseInt(this.delayPaymentDays) : null
						}
						: {})
				}
				if (this.sameDeliveryAddress) {
					const clientName = [payload.name, payload.surname].filter(Boolean).join(' ') || payload.nickname || null
					if (this.companyClient) {
						payload.deliveryName = payload.companyName || clientName
						payload.deliveryAddress = payload.companyAddress
						payload.deliveryCity = payload.companyCity
						payload.deliveryPostalCode = payload.companyPostalCode
						payload.deliveryCountry = payload.companyCountry
						payload.deliveryPhoneNumber = payload.phoneNumber
						payload.deliveryEmail = payload.email
					} else {
						payload.deliveryName = clientName
						payload.deliveryAddress = payload.address
						payload.deliveryCity = payload.city
						payload.deliveryPostalCode = payload.postalCode
						payload.deliveryCountry = payload.country
						payload.deliveryPhoneNumber = payload.phoneNumber
						payload.deliveryEmail = payload.email
					}
				}

				if (this.client && this.client.id) {
					//EDIT
					const _res = await this.editClient({ id: this.client.id, ...payload })
					this.requesting = false
					// this.$store.commit(
					// 	'Notification/ADD_NOTIFICATION',
					// 	this.$t('common.messages.update-success')
					// )
					// this.$store.dispatch('Clients/FETCH_CLIENTS') // Better solution??
					this.$emit('modal-toggle')
					this.$emit('client-edited', payload)
				} else {
					//CREATE
					const res = await this.addClient(payload)
					this.requesting = false
					this.$store.dispatch(
						'Notification/ADD_NOTIFICATION',
						{
							type: 'success',
							message: this.$t('clients-view.new-client.messages.success')
						}
					)
					if (this.refetch) {
						this.$store.dispatch('Clients/FETCH_CLIENTS') // Better solution??
					}
					this.$emit('modal-toggle')
					this.$emit('client-added', res)
				}
				this.clearClientForm({})
			} catch (e) {
				this.requesting = false
				if (e?.graphQLErrors?.[0]?.fields?.length) {
					const field = e.graphQLErrors[0].fields[0]
					const message = e.graphQLErrors[0].message
					if (this.$refs['clientInfo']) {
						this.$refs['clientInfo'].raiseFieldError(field, message)
					}
					this.$store.dispatch(
						'Notification/ADD_ERROR_NOTIFICATION',
						message
					)
				} else {
					this.$store.dispatch(
						'Notification/ADD_ERROR_NOTIFICATION',
						e
					)
				}
				this.$refs.modal.shake()
				return Promise.reject(e)
			}
		}
	}
}
</script>
<style lang="scss" module>
	.add-client-form {
		display: flex;
		flex-direction: column;
		gap: 0;

		&__title {
			margin-bottom: 0;
		}

		&__actions {
			display: flex;
			justify-content: flex-end;
			margin-top: $gutter * 2;
		}
	}

	.checkboxes {
		display: flex;
		flex-direction: column;
		margin-bottom: $gutter;

		&__item {
			display: flex;

			& + & {
				margin-top: $gutter;
			}

			& > * + * {
				margin-left: $gutter;
			}
		}
	}

	.create-part-view {
		display: block;
		width: 500px;
	}

</style>
